// src/pages/TransparencyPage.js
// Revisione 29/09/2026: pagina riscritta per lo stato attuale — titolare iscritto all'Albo,
// servizio di consulenza NON avviato. Rimosse le sezioni sul servizio (regime operativo, ambito
// dei servizi); remunerazione fee-only e conflitti mantenuti come modello "una volta iniziata
// l'attività" (decisione del titolare). Nome del titolare solo nella sezione iscrizione.
import React, { useContext, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { LangContext } from "../lang/LangProvider";
import { showOCFData, getRegistrationNamed, SERVICE_ACTIVE, getServiceStatusNotice } from "../config/compliance";
import Seo from "../components/Seo";
import "./TransparencyPage.css";

const TransparencyPage = () => {
  const { lang } = useContext(LangContext);

  // Link dal footer a #stato-servizio: lo ScrollToTop di App.js riporta in cima a ogni cambio
  // rotta (e gira dopo gli effect dei figli), quindi lo scroll all'ancora è differito.
  const { hash } = useLocation();
  useEffect(() => {
    if (hash !== "#stato-servizio") return undefined;
    const t = setTimeout(() => {
      const el = document.getElementById("stato-servizio");
      if (el) el.scrollIntoView({ block: "start" });
    }, 60);
    return () => clearTimeout(t);
  }, [hash]);

  const content = {
    it: {
      title: "Trasparenza",
      subtitle: "Stato del servizio e informazioni sul sito AlphaFrame Analytics",
      ocfTitle: "Iscrizione all'Albo del titolare",
      ocfBody: [
        getRegistrationNamed("it"),
        "AlphaFrame Analytics è il nome del sito: non è un soggetto iscritto all'Albo.",
        "Verifica pubblica dell'iscrizione sul sito dell'OCF: www.organismocf.it",
        "L'iscrizione all'Albo non comporta approvazione dei contenuti del sito da parte dell'OCF o della CONSOB.",
        "L'iscrizione è riferita all'ordinamento italiano: le informazioni del sito non implicano alcuna autorizzazione a operare in altri Paesi.",
      ],
      offerTitle: "Cosa offre il sito",
      offerItems: [
        "Articoli della Knowledge Base su mercati, strumenti e metodi quantitativi, a fini informativi ed educativi",
        "Serie video e testi (pubblicati anche sul canale YouTube)",
        "Materiali di studio per l'esame di abilitazione OCF",
        "Strumenti di simulazione con ipotesi dichiarate, eseguiti nel browser dell'utente",
        "Un recapito per informazioni generali e la possibilità di chiedere aggiornamenti sui nuovi contenuti",
      ],
      noScopeTitle: "Cosa il sito non consente",
      noScopeItems: [
        "Conferire incarichi di consulenza in materia di investimenti",
        "Ricevere raccomandazioni personalizzate su strumenti finanziari",
        "Svolgere valutazioni di adeguatezza: gli esercizi sul rischio presenti nel sito sono solo didattici",
        "Gestione di portafogli, ricezione o esecuzione di ordini, collocamento o distribuzione di prodotti finanziari",
        "Versare somme di denaro o affidare strumenti finanziari",
        "Consulenza assicurativa o previdenziale",
      ],
      educationTitle: "Nota sui contenuti educativi e sugli strumenti",
      educationBody:
        "Gli articoli, le serie e gli strumenti di simulazione hanno finalità informative ed educative. Non costituiscono consulenza in materia di investimenti né raccomandazioni personalizzate. I risultati dei simulatori dipendono dalle ipotesi indicate in ciascuno strumento, sono illustrativi e non rappresentano previsioni di rendimento. I rendimenti passati o simulati non sono indicativi di quelli futuri.",
      conflictsTitle: "Interessi e incentivi",
      conflictsBody:
        "Il titolare dichiara di non detenere partecipazioni rilevanti in intermediari finanziari e di non percepire incentivi da terze parti in relazione ai contenuti del sito.",
      remunerationTitle: "Remunerazione e conflitti d'interesse, una volta iniziata l'attività",
      remunerationBody: [
        "Oggi non sono percepiti compensi: il servizio di consulenza non è avviato.",
        "Una volta iniziata l'attività, la consulenza sarà prestata a pagamento (fee-only): remunerata esclusivamente dal cliente, senza commissioni di collocamento né retrocessioni da intermediari o produttori di strumenti finanziari.",
        "Una volta iniziata l'attività, in caso di potenziale conflitto d'interesse specifico il cliente verrà informato preventivamente per iscritto.",
      ],
      contactTitle: "Contatti generali",
      contactPre: "Per informazioni generali:",
      contactPost:
        "Il recapito non consente il conferimento di incarichi di consulenza né la richiesta di raccomandazioni personalizzate.",
    },
    en: {
      title: "Transparency",
      subtitle: "Service status and information about the AlphaFrame Analytics website",
      ocfTitle: "Owner's registration",
      ocfBody: [
        getRegistrationNamed("en"),
        "AlphaFrame Analytics is the name of the website: it is not a registered entity.",
        "Public verification of the registration on the OCF website: www.organismocf.it",
        "Registration does not imply approval of the website's content by the OCF or CONSOB.",
        "The registration refers to the Italian legal framework: the information on this website does not imply any authorisation to operate in other countries.",
      ],
      offerTitle: "What the website offers",
      offerItems: [
        "Knowledge Base articles on markets, instruments and quantitative methods, for informational and educational purposes",
        "Video and text series (also published on the YouTube channel)",
        "Study materials for the Italian OCF qualification exam",
        "Simulation tools with stated assumptions, running in the user's browser",
        "A contact for general information and the option to ask for updates on new content",
      ],
      noScopeTitle: "What the website does not allow",
      noScopeItems: [
        "Conferring investment advisory engagements",
        "Receiving personalised recommendations on financial instruments",
        "Suitability assessments: the risk exercises on this website are for teaching only",
        "Portfolio management, receiving or executing orders, placing or distributing financial products",
        "Paying in money or entrusting financial instruments",
        "Insurance or pension advice",
      ],
      educationTitle: "Note on educational content and tools",
      educationBody:
        "Articles, series and simulation tools are for informational and educational purposes. They do not constitute investment advice or personalised recommendations. Simulator results depend on the assumptions stated in each tool, are illustrative and do not represent performance forecasts. Past or simulated returns are not indicative of future ones.",
      conflictsTitle: "Interests and incentives",
      conflictsBody:
        "The owner declares that they hold no material shareholdings in financial intermediaries and receive no incentives from third parties in connection with the website's content.",
      remunerationTitle: "Remuneration and conflicts of interest, once the activity has started",
      remunerationBody: [
        "No fees are received today: the investment advice service has not commenced.",
        "Once the activity has started, advice will be provided for a fee (fee-only): paid exclusively by the client, with no placement commissions or retrocessions from intermediaries or financial product manufacturers.",
        "Once the activity has started, in the event of a specific potential conflict of interest the client will be informed in advance and in writing.",
      ],
      contactTitle: "General contact",
      contactPre: "For general information:",
      contactPost:
        "This contact cannot be used to confer advisory engagements or to request personalised recommendations.",
    },
  };

  const c = lang === "en" ? content.en : content.it;
  const notice = getServiceStatusNotice(lang);

  return (
    <>
      <Seo
        title={c.title}
        description={
          lang === "en"
            ? "Transparency: service status (the investment advice service has not yet commenced), the owner's registration in the Italian register, what the website offers and does not allow."
            : "Trasparenza: stato del servizio (consulenza in materia di investimenti non avviata), iscrizione all'Albo del titolare, cosa offre e cosa non consente il sito."
        }
        path={`/${lang}/transparency`}
      />
      <div className="transp-bg">
        <div className="transp-container">
          <h1 className="transp-title">{c.title}</h1>
          <p className="transp-subtitle">{c.subtitle}</p>

          {/* Stato del servizio: sezione allineata alle altre (niente riquadro), testo esatto dal config */}
          {!SERVICE_ACTIVE && (
            <section className="transp-section" id="stato-servizio">
              <h2>{notice.title}</h2>
              {notice.paras.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </section>
          )}

          {showOCFData && (
            <section className="transp-section transp-ocf">
              <h2>{c.ocfTitle}</h2>
              <ul>
                {c.ocfBody.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>
            </section>
          )}

          <section className="transp-section">
            <h2>{c.offerTitle}</h2>
            <ul>
              {c.offerItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="transp-section">
            <h2>{c.noScopeTitle}</h2>
            <ul className="transp-noscope">
              {c.noScopeItems.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </section>

          <section className="transp-section">
            <h2>{c.educationTitle}</h2>
            <p>{c.educationBody}</p>
          </section>

          <section className="transp-section">
            <h2>{c.conflictsTitle}</h2>
            <p>{c.conflictsBody}</p>
          </section>

          <section className="transp-section">
            <h2>{c.remunerationTitle}</h2>
            {c.remunerationBody.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </section>

          <section className="transp-section">
            <h2>{c.contactTitle}</h2>
            <p>
              {c.contactPre}{" "}
              <a href="mailto:alphaframeanalytics.it@gmail.com" className="transp-email">
                alphaframeanalytics.it@gmail.com
              </a>
            </p>
            <p>{c.contactPost}</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default TransparencyPage;
